import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** Matches the app's phone layout breakpoint (max-width: 768px). */
const DESKTOP_QUERY = '(min-width: 769px)';
const MIN_WIDTH = 280;
const MIN_HEIGHT = 180;
const BOUNDS_GAP = 8;

const SURFACE_SELECTOR = [
  '.MuiDialog-paper:not(.MuiDialog-paperFullScreen)',
  '.modal-container',
  '.MuiModal-root:not(.MuiDialog-root) [class*="-modal-content"]'
].join(', ');

const GRIP_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M12 2 L2 12 M12 7 L7 12 M12 11 L11 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg>';

function collectSurfaces() {
  if (typeof document === 'undefined') return [];
  if (document.body.classList.contains('popout-mode')) return [];
  if (!window.matchMedia(DESKTOP_QUERY).matches) return [];

  const nodes = [...document.querySelectorAll(SURFACE_SELECTOR)];
  const chosen = [];
  for (const el of nodes) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.classList.contains('MuiDialog-paperFullScreen')) continue;
    if (chosen.some((parent) => parent !== el && parent.contains(el))) continue;
    chosen.push(el);
  }
  return chosen;
}

function layoutBounds() {
  const column = document.querySelector('.layout-main');
  if (column) {
    const rect = column.getBoundingClientRect();
    return {
      left: rect.left + BOUNDS_GAP,
      top: rect.top + BOUNDS_GAP,
      right: rect.right - BOUNDS_GAP,
      bottom: Math.min(window.innerHeight, rect.bottom) - BOUNDS_GAP
    };
  }
  return {
    left: BOUNDS_GAP,
    top: BOUNDS_GAP,
    right: window.innerWidth - BOUNDS_GAP,
    bottom: window.innerHeight - BOUNDS_GAP
  };
}

function clampBox(left, top, width, height, bounds) {
  const availW = Math.max(0, bounds.right - bounds.left);
  const availH = Math.max(0, bounds.bottom - bounds.top);
  const minW = Math.min(MIN_WIDTH, availW);
  const minH = Math.min(MIN_HEIGHT, availH);
  const nextW = Math.min(availW, Math.max(minW, width));
  const nextH = Math.min(availH, Math.max(minH, height));
  const nextLeft = Math.min(Math.max(bounds.left, left), bounds.right - nextW);
  const nextTop = Math.min(Math.max(bounds.top, top), bounds.bottom - nextH);
  return {
    left: Math.round(nextLeft),
    top: Math.round(nextTop),
    width: Math.round(nextW),
    height: Math.round(nextH)
  };
}

function applyBox(el, box) {
  const set = (prop, value) => el.style.setProperty(prop, value, 'important');
  el.dataset.appModalSized = '1';
  set('position', 'fixed');
  set('left', `${box.left}px`);
  set('top', `${box.top}px`);
  set('right', 'auto');
  set('bottom', 'auto');
  set('width', `${box.width}px`);
  set('height', `${box.height}px`);
  set('max-width', 'none');
  set('max-height', 'none');
  set('margin', '0');
  set('transform', 'none');
  set('transition', 'none');
  el.classList.add('app-modal-resized');
}

function clearSizedStyles(el) {
  if (el.dataset.appModalSized !== '1') return;
  ['position', 'left', 'top', 'right', 'bottom', 'width', 'height', 'max-width', 'max-height', 'margin', 'transform', 'transition'].forEach((prop) => {
    el.style.removeProperty(prop);
  });
  delete el.dataset.appModalSized;
  el.classList.remove('app-modal-resized');
}

let resizing = false;

function reclampResized() {
  if (resizing) return;
  const bounds = layoutBounds();
  document.querySelectorAll('.app-modal-resized').forEach((el) => {
    const rect = el.getBoundingClientRect();
    applyBox(el, clampBox(rect.left, rect.top, rect.width, rect.height, bounds));
  });
}

function resizeFromDrag(origin, dx, dy, bounds) {
  const minW = Math.min(MIN_WIDTH, Math.max(0, bounds.right - bounds.left));
  const minH = Math.min(MIN_HEIGHT, Math.max(0, bounds.bottom - bounds.top));
  const originRight = origin.left + origin.width;
  const originBottom = origin.top + origin.height;
  const roomRight = Math.max(0, bounds.right - originRight);
  const roomLeft = Math.max(0, origin.left - bounds.left);
  const roomBottom = Math.max(0, bounds.bottom - originBottom);
  const roomTop = Math.max(0, origin.top - bounds.top);

  let left = origin.left;
  let width = origin.width;
  if (dx >= 0) {
    const travel = Math.min(dx, roomRight);
    const extra = roomRight > 0 ? (travel / roomRight) * (roomRight + roomLeft) : Math.min(dx, roomLeft);
    width = origin.width + extra;
    left = originRight + travel - width;
  } else {
    width = Math.max(minW, origin.width + dx);
  }

  let top = origin.top;
  let height = origin.height;
  if (dy >= 0) {
    const travel = Math.min(dy, roomBottom);
    const extra = roomBottom > 0 ? (travel / roomBottom) * (roomBottom + roomTop) : Math.min(dy, roomTop);
    height = origin.height + extra;
    top = originBottom + travel - height;
  } else {
    height = Math.max(minH, origin.height + dy);
  }

  return clampBox(left, top, width, height, bounds);
}

function startResize(event, el) {
  if (event.button != null && event.button !== 0) return;
  event.preventDefault();
  event.stopPropagation();

  const rect = el.getBoundingClientRect();
  const fitted = clampBox(rect.left, rect.top, rect.width, rect.height, layoutBounds());
  applyBox(el, fitted);
  const originX = event.clientX;
  const originY = event.clientY;
  const origin = { left: fitted.left, top: fitted.top, width: fitted.width, height: fitted.height };
  resizing = true;

  const move = (ev) => {
    applyBox(el, resizeFromDrag(origin, ev.clientX - originX, ev.clientY - originY, layoutBounds()));
  };

  const end = () => {
    resizing = false;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', end);
    window.removeEventListener('pointercancel', end);
    document.body.style.userSelect = '';
    reclampResized();
  };

  document.body.style.userSelect = 'none';
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
  event.currentTarget.setPointerCapture?.(event.pointerId);
}

function clearHandles() {
  document.querySelectorAll('.app-modal-resize-handle').forEach((node) => node.remove());
  document.querySelectorAll('.app-resizable-modal').forEach((el) => {
    el.classList.remove('app-resizable-modal');
    delete el.dataset.appModalPos;
  });
}

/**
 * Adds a drag corner to open dialogs and form modals on larger screens.
 * Phones keep the fixed responsive sizes. The grip is plain DOM so a dialog
 * re-render does not throw it away.
 */
export default function ResizableModals() {
  const [tip, setTip] = useState(null);

  useEffect(() => {
    let frame = 0;

    const showTip = (event) => {
      setTip({
        x: event.clientX + 12,
        y: event.clientY + 14
      });
    };
    const moveTip = (event) => {
      setTip((prev) => (prev ? { x: event.clientX + 12, y: event.clientY + 14 } : prev));
    };
    const hideTip = () => setTip(null);
    const hideTipUnlessOverHandle = (event) => {
      const overHandle = event.target instanceof Element
        && event.target.closest('.app-modal-resize-handle');
      if (!overHandle) setTip((prev) => (prev ? null : prev));
    };
    const hideTipOnWindowLeave = () => hideTip();
    document.addEventListener('pointermove', hideTipUnlessOverHandle, true);
    document.addEventListener('pointerdown', hideTipUnlessOverHandle, true);
    document.documentElement.addEventListener('mouseleave', hideTipOnWindowLeave);
    window.addEventListener('scroll', hideTip, true);
    window.addEventListener('blur', hideTip);

    const ensureHandle = (el) => {
      el.classList.add('app-resizable-modal');
      if (window.getComputedStyle(el).position === 'static') {
        el.dataset.appModalPos = 'relative';
      }
      if (el.querySelector(':scope > .app-modal-resize-handle')) return;
      const handle = document.createElement('div');
      handle.className = 'app-modal-resize-handle';
      handle.setAttribute('role', 'button');
      handle.setAttribute('aria-label', 'Drag to resize');
      handle.innerHTML = GRIP_SVG;
      handle.addEventListener('pointerdown', (event) => {
        hideTip();
        startResize(event, el);
      });
      handle.addEventListener('mouseenter', showTip);
      handle.addEventListener('mousemove', moveTip);
      handle.addEventListener('mouseleave', hideTip);
      el.appendChild(handle);
    };

    const refresh = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!window.matchMedia(DESKTOP_QUERY).matches || document.body.classList.contains('popout-mode')) {
          document.querySelectorAll('[data-app-modal-sized="1"]').forEach(clearSizedStyles);
          clearHandles();
          hideTip();
          return;
        }
        const surfaces = collectSurfaces();
        const keep = new Set(surfaces);
        document.querySelectorAll('.app-resizable-modal').forEach((el) => {
          if (!keep.has(el)) {
            el.querySelector(':scope > .app-modal-resize-handle')?.remove();
            el.classList.remove('app-resizable-modal');
            delete el.dataset.appModalPos;
          }
        });
        surfaces.forEach(ensureHandle);
      });
    };

    let observedSidebar = null;
    const sidebarObserver = new ResizeObserver(() => reclampResized());
    const watchSidebar = () => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar === observedSidebar) return;
      sidebarObserver.disconnect();
      observedSidebar = sidebar;
      if (sidebar) sidebarObserver.observe(sidebar);
    };
    const refreshAndWatch = () => {
      watchSidebar();
      refresh();
    };

    const media = window.matchMedia(DESKTOP_QUERY);
    const observer = new MutationObserver(refreshAndWatch);
    observer.observe(document.body, { childList: true, subtree: true });
    media.addEventListener('change', refreshAndWatch);
    window.addEventListener('resize', reclampResized);
    refreshAndWatch();

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('pointermove', hideTipUnlessOverHandle, true);
      document.removeEventListener('pointerdown', hideTipUnlessOverHandle, true);
      document.documentElement.removeEventListener('mouseleave', hideTipOnWindowLeave);
      window.removeEventListener('scroll', hideTip, true);
      window.removeEventListener('blur', hideTip);
      observer.disconnect();
      sidebarObserver.disconnect();
      media.removeEventListener('change', refreshAndWatch);
      window.removeEventListener('resize', reclampResized);
      clearHandles();
    };
  }, []);

  if (!tip) return null;

  return createPortal(
    <div
      className="app-cursor-tooltip"
      role="tooltip"
      style={{
        position: 'fixed',
        left: tip.x,
        top: tip.y,
        zIndex: 10000,
        pointerEvents: 'none',
        maxWidth: 280,
        padding: '6px 8px',
        borderRadius: 6,
        border: '1px solid var(--color-border)',
        background: 'var(--color-bg-alt)',
        color: 'var(--color-text)',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.3,
        boxShadow: '0 4px 14px rgba(0,0,0,0.25)'
      }}
    >
      Drag to resize
    </div>,
    document.body
  );
}
